import React, { useEffect, useState } from 'react';
import { Modal, View, TouchableOpacity, Image, Dimensions, Platform } from 'react-native';
import Typography from 'src/components/Text/Text';
import Button from 'src/components/Button/Button';
import CustomMap from 'src/components/Map/Map';
import { styles } from './MapModal.styles';
import closeIcon from 'src/assets/close.png';
import { Address } from '@typeRoots/common/address';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store/store';
import { mapFetchApi } from 'src/helpers/addressHelper';

interface MapModalProps {
  isVisible: boolean;
  onClose: () => void;
  onSave: (currentAddress: Address) => void;
  address: Address;
  initialPosition: { lat: number; lng: number };
  title?: string;
  testID?: string;
}

const { height } = Dimensions.get('window'); // For dynamic height

const MapModal: React.FC<MapModalProps> = ({
  isVisible,
  onClose,
  onSave,
  address,
  initialPosition,
  title = 'Add Garaging Address',
  testID,
}) => {
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  useEffect(() => {
    if (Platform.OS !== 'web') return;

    let originalHeight = window.innerHeight;

    const handleResize = () => {
      const currentHeight = window.innerHeight;
      setKeyboardOpen(currentHeight < originalHeight - 100); // adjust sensitivity if needed
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') {
      document.body.style.overflow = isVisible ? 'hidden' : '';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isVisible]);
  const token = useSelector((state: RootState) => state.users.userToken); // Get the token from Redux store
  const tpJwt = useSelector((state: RootState) => state.UI.tpJwtToken); // Get the token from Redux store
  const [currentAddress, setCurrentAddress] = useState(address);
  const [formattedAddress, setFormattedAddress] = useState<{ line1: string; line2: string }>({
    line1: '',
    line2: '',
  });

  const getLatLng = async () => {
    try {
      // Create a formatted address string
      const formattedAddress = [
        address.street,
        address.city,
        address.state,
        address.zipCode,
        address.county,
      ]
        .filter(Boolean) // Remove empty or null values
        .join(', '); // Join with commas

      // Call Google Maps Geocoding API
      const url = token
        ? `${process.env.REACT_APP_API_BASE_URL}/maps/geocode/address?address=${encodeURIComponent(formattedAddress)}`
        : `${process.env.REACT_APP_API_BASE_URL}/auth/address?address=${encodeURIComponent(formattedAddress)}&tpJwt=${tpJwt}`;

      const response = await mapFetchApi(token, url);

      const data = await response.json();
      if (data.status === 'OK' && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        setCurrentAddress({
          ...currentAddress,
          latitude: lat,
          longitude: lng,
        });
      } else {
        console.error('Error fetching geocode:', data.error_message || data.status);
      }
    } catch (error) {
      console.error('Failed to fetch latitude and longitude:', error);
    }
  };

  useEffect(() => {
    if (address.latitude !== 0 && address.longitude !== 0) {
      const line1 = `${address.street}`.trim(); // First line: street address
      const line2 = `${address.city}, ${address.state} ${address.zipCode}`.trim(); // Second line: city, state, ZIP
      setFormattedAddress({ line1, line2 });
      return;
    }
    const fetchLatLng = async () => {
      await getLatLng();
    };
    fetchLatLng();
  }, [isVisible, address]); // Empty dependency array ensures it runs only once

  return (
    <Modal visible={isVisible} transparent animationType="slide">
      <View
        style={[
          styles.modalOverlay,
          Platform.OS === 'web' && {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9999,
            justifyContent: 'flex-end',
          },
        ]}>
        <View
          style={[
            styles.modalContainer,
            { height: height * 0.9 },
            Platform.OS === 'web' && keyboardOpen && { transform: [{ translateY: 250 }] },
          ]}>
          {/* divider */}
          <View style={styles.divider} />
          {/* Header */}
          <View style={styles.header}>
            <Typography variant="h2">{title}</Typography>
            <TouchableOpacity onPress={onClose} style={styles.closeIcon}>
              <Image
                source={closeIcon}
                style={styles.closeImage}
                accessibilityLabel={`${testID}-close-icon`}
              />
            </TouchableOpacity>
          </View>

          {/* Subtitle */}
          <View style={styles.subtitleContainer}>
            <Typography variant="body2" style={styles.subtitleText}>
              Please find or update your address using search bar
            </Typography>
          </View>

          {/* Map Component */}
          <View style={styles.mapContainer}>
            <CustomMap
              testID={testID}
              setFormattedAddress={setFormattedAddress}
              mapAddress={currentAddress}
              setMapAddress={setCurrentAddress}
              initialPosition={initialPosition}
            />
          </View>

          {/* Address Footer */}
          <View style={styles.footerContainer}>
            <View style={styles.addressContainer}>
              {/* console log address here */}
              <Typography variant="h4">{formattedAddress.line1}</Typography>
              <Typography variant="body2" style={styles.addressText}>
                {formattedAddress.line2}
              </Typography>
            </View>
          </View>

          {/* Buttons */}
          <View style={styles.buttonContainer}>
            <Button
              testID={`${testID}-cancel-btn`}
              type="tertiary"
              title="Cancel"
              onPress={onClose}
              style={styles.buttonWrapper1}
            />
            <Button
              testID={`${testID}-save-btn`}
              type="primary"
              title="Save Address"
              onPress={() => {
                //setAddress(currentAddress)
                onSave(currentAddress);
              }}
              style={styles.buttonWrapper2}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default MapModal;
